import React from "react";
import clsx, { type ClassValue, } from "clsx";
import { twMerge, } from "tailwind-merge";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// MUI default theme.spacing = 8px. Aceita number (n * 8px) ou string (CSS literal).
type Spacing = number | string;
type CssSize = number | string;

// Converte number -> "Npx" e string -> string (CSS literal: "50%", "30px 0 20px", etc.).
function toCss(v: number | string | undefined): string | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v === "number") return `${v}px`;

  return v;
}

function toSpacing(v: Spacing | undefined): string | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v === "number") return `${v * 8}px`;

  return v;
}

// Props "de sistema" suportadas (subconjunto pragmático do MUI Box v4).
export interface BoxSystemProps {
  // Margin shorthand (numbers usam theme.spacing = 8px)
  m?: Spacing;
  mt?: Spacing;
  mr?: Spacing;
  mb?: Spacing;
  ml?: Spacing;
  mx?: Spacing;
  my?: Spacing;
  // Padding shorthand
  p?: Spacing;
  pt?: Spacing;
  pr?: Spacing;
  pb?: Spacing;
  pl?: Spacing;
  px?: Spacing;
  py?: Spacing;
  // Margin/padding diretos (sobrescrevem o shorthand)
  margin?: CssSize;
  marginTop?: CssSize;
  marginRight?: CssSize;
  marginBottom?: CssSize;
  marginLeft?: CssSize;
  padding?: CssSize;
  paddingTop?: CssSize;
  paddingRight?: CssSize;
  paddingBottom?: CssSize;
  paddingLeft?: CssSize;
  // Flex / display
  display?: React.CSSProperties["display"];
  flexDirection?: React.CSSProperties["flexDirection"];
  flexWrap?: React.CSSProperties["flexWrap"];
  justifyContent?: React.CSSProperties["justifyContent"];
  alignItems?: React.CSSProperties["alignItems"];
  alignContent?: React.CSSProperties["alignContent"];
  alignSelf?: React.CSSProperties["alignSelf"];
  flex?: React.CSSProperties["flex"];
  flexGrow?: React.CSSProperties["flexGrow"];
  flexShrink?: React.CSSProperties["flexShrink"];
  flexBasis?: React.CSSProperties["flexBasis"];
  order?: React.CSSProperties["order"];
  gap?: Spacing;
  rowGap?: Spacing;
  columnGap?: Spacing;
  // Sizing
  width?: CssSize;
  height?: CssSize;
  minWidth?: CssSize;
  maxWidth?: CssSize;
  minHeight?: CssSize;
  maxHeight?: CssSize;
  // Cores
  color?: string;
  bgcolor?: string;
  // Tipografia
  fontFamily?: React.CSSProperties["fontFamily"];
  fontSize?: CssSize;
  fontWeight?: React.CSSProperties["fontWeight"];
  lineHeight?: React.CSSProperties["lineHeight"];
  letterSpacing?: React.CSSProperties["letterSpacing"];
  textAlign?: React.CSSProperties["textAlign"];
  textTransform?: React.CSSProperties["textTransform"];
  // Borda
  border?: React.CSSProperties["border"];
  borderTop?: React.CSSProperties["borderTop"];
  borderRight?: React.CSSProperties["borderRight"];
  borderBottom?: React.CSSProperties["borderBottom"];
  borderLeft?: React.CSSProperties["borderLeft"];
  borderColor?: React.CSSProperties["borderColor"];
  borderRadius?: CssSize;
  // Posicionamento
  position?: React.CSSProperties["position"];
  top?: CssSize;
  right?: CssSize;
  bottom?: CssSize;
  left?: CssSize;
  zIndex?: React.CSSProperties["zIndex"];
  // Overflow / visibilidade
  overflow?: React.CSSProperties["overflow"];
  overflowX?: React.CSSProperties["overflowX"];
  overflowY?: React.CSSProperties["overflowY"];
  visibility?: React.CSSProperties["visibility"];
  // Misc
  boxShadow?: React.CSSProperties["boxShadow"];
  opacity?: React.CSSProperties["opacity"];
  cursor?: React.CSSProperties["cursor"];
}

// Chaves system para extrair das props antes de espalhar no DOM.
const SYSTEM_KEYS: ReadonlyArray<keyof BoxSystemProps> = [
  "m", "mt", "mr", "mb", "ml", "mx", "my",
  "p", "pt", "pr", "pb", "pl", "px", "py",
  "margin", "marginTop", "marginRight", "marginBottom", "marginLeft",
  "padding", "paddingTop", "paddingRight", "paddingBottom", "paddingLeft",
  "display", "flexDirection", "flexWrap", "justifyContent", "alignItems",
  "alignContent", "alignSelf", "flex", "flexGrow", "flexShrink", "flexBasis",
  "order", "gap", "rowGap", "columnGap",
  "width", "height", "minWidth", "maxWidth", "minHeight", "maxHeight",
  "color", "bgcolor",
  "fontFamily", "fontSize", "fontWeight", "lineHeight", "letterSpacing",
  "textAlign", "textTransform",
  "border", "borderTop", "borderRight", "borderBottom", "borderLeft",
  "borderColor", "borderRadius",
  "position", "top", "right", "bottom", "left", "zIndex",
  "overflow", "overflowX", "overflowY", "visibility",
  "boxShadow", "opacity", "cursor",
];

function buildStyle(p: BoxSystemProps): React.CSSProperties {
  const s: React.CSSProperties = {};

  // Spacing shorthand (number * 8px; string literal)
  // mx/my expandem para left+right / top+bottom; m expande para todos.
  // Direct keys (marginTop, padding, etc.) sobrescrevem o shorthand.
  if (p.m !== undefined) s.margin = toSpacing(p.m);
  if (p.mx !== undefined) {
    s.marginLeft = toSpacing(p.mx);
    s.marginRight = toSpacing(p.mx);
  }
  if (p.my !== undefined) {
    s.marginTop = toSpacing(p.my);
    s.marginBottom = toSpacing(p.my);
  }
  if (p.mt !== undefined) s.marginTop = toSpacing(p.mt);
  if (p.mr !== undefined) s.marginRight = toSpacing(p.mr);
  if (p.mb !== undefined) s.marginBottom = toSpacing(p.mb);
  if (p.ml !== undefined) s.marginLeft = toSpacing(p.ml);

  if (p.p !== undefined) s.padding = toSpacing(p.p);
  if (p.px !== undefined) {
    s.paddingLeft = toSpacing(p.px);
    s.paddingRight = toSpacing(p.px);
  }
  if (p.py !== undefined) {
    s.paddingTop = toSpacing(p.py);
    s.paddingBottom = toSpacing(p.py);
  }
  if (p.pt !== undefined) s.paddingTop = toSpacing(p.pt);
  if (p.pr !== undefined) s.paddingRight = toSpacing(p.pr);
  if (p.pb !== undefined) s.paddingBottom = toSpacing(p.pb);
  if (p.pl !== undefined) s.paddingLeft = toSpacing(p.pl);

  // Direct margin/padding (literal CSS, sobrescrevem o shorthand)
  if (p.margin !== undefined) s.margin = toCss(p.margin);
  if (p.marginTop !== undefined) s.marginTop = toCss(p.marginTop);
  if (p.marginRight !== undefined) s.marginRight = toCss(p.marginRight);
  if (p.marginBottom !== undefined) s.marginBottom = toCss(p.marginBottom);
  if (p.marginLeft !== undefined) s.marginLeft = toCss(p.marginLeft);
  if (p.padding !== undefined) s.padding = toCss(p.padding);
  if (p.paddingTop !== undefined) s.paddingTop = toCss(p.paddingTop);
  if (p.paddingRight !== undefined) s.paddingRight = toCss(p.paddingRight);
  if (p.paddingBottom !== undefined) s.paddingBottom = toCss(p.paddingBottom);
  if (p.paddingLeft !== undefined) s.paddingLeft = toCss(p.paddingLeft);

  // Flex / display (passthrough)
  if (p.display !== undefined) s.display = p.display;
  if (p.flexDirection !== undefined) s.flexDirection = p.flexDirection;
  if (p.flexWrap !== undefined) s.flexWrap = p.flexWrap;
  if (p.justifyContent !== undefined) s.justifyContent = p.justifyContent;
  if (p.alignItems !== undefined) s.alignItems = p.alignItems;
  if (p.alignContent !== undefined) s.alignContent = p.alignContent;
  if (p.alignSelf !== undefined) s.alignSelf = p.alignSelf;
  if (p.flex !== undefined) s.flex = p.flex;
  if (p.flexGrow !== undefined) s.flexGrow = p.flexGrow;
  if (p.flexShrink !== undefined) s.flexShrink = p.flexShrink;
  if (p.flexBasis !== undefined) s.flexBasis = p.flexBasis as React.CSSProperties["flexBasis"];
  if (p.order !== undefined) s.order = p.order;
  if (p.gap !== undefined) s.gap = toSpacing(p.gap);
  if (p.rowGap !== undefined) s.rowGap = toSpacing(p.rowGap);
  if (p.columnGap !== undefined) s.columnGap = toSpacing(p.columnGap);

  // Sizing (number -> Npx; string passa direto)
  if (p.width !== undefined) s.width = toCss(p.width);
  if (p.height !== undefined) s.height = toCss(p.height);
  if (p.minWidth !== undefined) s.minWidth = toCss(p.minWidth);
  if (p.maxWidth !== undefined) s.maxWidth = toCss(p.maxWidth);
  if (p.minHeight !== undefined) s.minHeight = toCss(p.minHeight);
  if (p.maxHeight !== undefined) s.maxHeight = toCss(p.maxHeight);

  // Cores
  if (p.color !== undefined) s.color = p.color;
  if (p.bgcolor !== undefined) s.backgroundColor = p.bgcolor;

  // Tipografia
  if (p.fontFamily !== undefined) s.fontFamily = p.fontFamily;
  if (p.fontSize !== undefined) s.fontSize = toCss(p.fontSize);
  if (p.fontWeight !== undefined) s.fontWeight = p.fontWeight;
  if (p.lineHeight !== undefined) s.lineHeight = p.lineHeight;
  if (p.letterSpacing !== undefined) s.letterSpacing = p.letterSpacing;
  if (p.textAlign !== undefined) s.textAlign = p.textAlign;
  if (p.textTransform !== undefined) s.textTransform = p.textTransform;

  // Borda
  if (p.border !== undefined) s.border = p.border;
  if (p.borderTop !== undefined) s.borderTop = p.borderTop;
  if (p.borderRight !== undefined) s.borderRight = p.borderRight;
  if (p.borderBottom !== undefined) s.borderBottom = p.borderBottom;
  if (p.borderLeft !== undefined) s.borderLeft = p.borderLeft;
  if (p.borderColor !== undefined) s.borderColor = p.borderColor;
  if (p.borderRadius !== undefined) s.borderRadius = toCss(p.borderRadius);

  // Posicionamento
  if (p.position !== undefined) s.position = p.position;
  if (p.top !== undefined) s.top = toCss(p.top);
  if (p.right !== undefined) s.right = toCss(p.right);
  if (p.bottom !== undefined) s.bottom = toCss(p.bottom);
  if (p.left !== undefined) s.left = toCss(p.left);
  if (p.zIndex !== undefined) s.zIndex = p.zIndex;

  // Overflow / visibilidade
  if (p.overflow !== undefined) s.overflow = p.overflow;
  if (p.overflowX !== undefined) s.overflowX = p.overflowX;
  if (p.overflowY !== undefined) s.overflowY = p.overflowY;
  if (p.visibility !== undefined) s.visibility = p.visibility;

  // Misc
  if (p.boxShadow !== undefined) s.boxShadow = p.boxShadow;
  if (p.opacity !== undefined) s.opacity = p.opacity;
  if (p.cursor !== undefined) s.cursor = p.cursor;

  return s;
}

// Props próprias (não-system) do Box.
interface BoxOwnProps extends BoxSystemProps {
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  clone?: boolean;
}

// Componente polimórfico (mesma estratégia do Container).
export type BoxProps<C extends React.ElementType = "div"> =
  BoxOwnProps
  & { component?: C }
  & Omit<React.ComponentPropsWithRef<C>, keyof BoxOwnProps | "component">;

type BoxComponent = <C extends React.ElementType = "div">(
  props: BoxProps<C>
) => React.ReactElement | null;

const Box = React.forwardRef(function Box<C extends React.ElementType = "div">(
  props: BoxProps<C>,
  ref: React.ComponentPropsWithRef<C>["ref"]
) {
  // Separa system props das demais.
  const rest = { ...(props as Record<string, unknown>) };
  const systemProps: BoxSystemProps = {};

  for (const key of SYSTEM_KEYS) {
    if (key in rest) {
      (systemProps as Record<string, unknown>)[key] = rest[key];
      delete rest[key];
    }
  }

  const {
    component,
    className,
    style,
    clone = false,
    children,
    ...other
  } = rest as BoxOwnProps & { component?: React.ElementType; [k: string]: unknown };

  const systemStyle = buildStyle(systemProps);
  const finalStyle: React.CSSProperties = { ...systemStyle, ...(style || {}) };

  // `clone`: copia as props no único filho ReactElement, sem renderizar wrapper.
  if (clone && React.isValidElement(children)) {
    const child = children as React.ReactElement<{ className?: string; style?: React.CSSProperties; }>;

    return React.cloneElement(child, {
      ...other,
      className: cn(child.props.className, className),
      style: { ...(child.props.style || {}), ...finalStyle },
    } as Partial<typeof child.props>);
  }

  const Component = (component ?? "div") as React.ElementType;

  return (
    <Component
      ref={ref}
      className={cn(className)}
      style={finalStyle}
      {...other}
    >
      {children}
    </Component>
  );
}) as BoxComponent & { displayName?: string };

Box.displayName = "Box";

export default Box;
